import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { personalMyTodoAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate, parseDateOnlyLocalMs } from '../utils/dateFormat';
import ContentHeader from '../components/ContentHeader';
import DeleteIconButton from '../components/DeleteIconButton';
import './PersonalMyTodos.css';

const SORT_KEYS = {
  title: 'title',
  status: 'status',
  due_date: 'due_date',
  visible_from_date: 'visible_from_date',
  created_at: 'created_at',
};

function statusRank(status) {
  if (status === 'open') return 0;
  if (status === 'done') return 1;
  if (status === 'canceled') return 2;
  return 3;
}

function dateFieldMs(value) {
  if (value == null || value === '') return null;
  const ms = parseDateOnlyLocalMs(value);
  return ms || null;
}

function createdAtMs(value) {
  if (value == null || value === '') return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Date columns: nulls always last; asc = oldest first, desc = newest first. */
function compareNullableDateMs(aMs, bMs, sortDir) {
  const aN = aMs == null;
  const bN = bMs == null;
  if (aN && bN) return 0;
  if (aN) return 1;
  if (bN) return -1;
  const raw = aMs - bMs;
  return sortDir === 'asc' ? raw : -raw;
}

function comparePersonalRows(a, b, sortKey, sortDir) {
  let cmp = 0;
  switch (sortKey) {
    case SORT_KEYS.title:
      cmp = (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    case SORT_KEYS.status:
      cmp = statusRank(a.status) - statusRank(b.status);
      return sortDir === 'asc' ? cmp : -cmp;
    case SORT_KEYS.due_date:
      return compareNullableDateMs(dateFieldMs(a.due_date), dateFieldMs(b.due_date), sortDir);
    case SORT_KEYS.visible_from_date:
      return compareNullableDateMs(
        dateFieldMs(a.visible_from_date),
        dateFieldMs(b.visible_from_date),
        sortDir
      );
    case SORT_KEYS.created_at:
      return compareNullableDateMs(createdAtMs(a.created_at), createdAtMs(b.created_at), sortDir);
    default:
      return 0;
  }
}

function emptyForm() {
  return {
    title: '',
    description: '',
    due_date: '',
    visible_from_date: '',
    status: 'open',
    repeat_interval: 'none',
    repeat_end_on: '',
    repeat_max_occurrences: '',
  };
}

function addCalendarDaysLocal(days) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultOneYearFromTodayLocal() {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setFullYear(d.getFullYear() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function repeatLabel(interval) {
  if (interval === 'weekly') return 'Weekly';
  if (interval === 'monthly') return 'Monthly';
  if (interval === 'quarterly') return 'Quarterly';
  return '—';
}

function rowToFormData(row) {
  return {
    title: row.title || '',
    description: row.description || '',
    due_date: row.due_date || '',
    visible_from_date: row.visible_from_date || '',
    status: row.status || 'open',
    repeat_interval: row.repeat_interval === 'weekly' || row.repeat_interval === 'monthly' || row.repeat_interval === 'quarterly' ? row.repeat_interval : 'none',
    repeat_end_on: row.repeat_end_on || '',
    repeat_max_occurrences:
      row.repeat_max_occurrences != null && row.repeat_max_occurrences !== '' ? String(row.repeat_max_occurrences) : '',
  };
}

/**
 * Not-client-specific My To-Dos (left nav). Work tasks and client-linked items belong under a client.
 */
function PersonalMyTodos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openTaskIdFromUrl = searchParams.get('taskId') || '';
  const openedTaskIdRef = useRef(null);
  const statusWhenEditStartedRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [sortKey, setSortKey] = useState(SORT_KEYS.created_at);
  const [sortDir, setSortDir] = useState('desc');
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [followUpChecked, setFollowUpChecked] = useState(false);
  const [followUpDueDate, setFollowUpDueDate] = useState(() => addCalendarDaysLocal(30));

  const canCreate = hasPermission('client_work_tasks.create');
  const canUpdate = hasPermission('client_work_tasks.update');
  const canDelete = hasPermission('client_work_tasks.delete');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await personalMyTodoAPI.list({
        params: showCompleted ? { includeCompleted: 'true' } : {},
      });
      setItems(res.data?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [showCompleted]);

  useEffect(() => {
    if (!openTaskIdFromUrl) {
      openedTaskIdRef.current = null;
      return;
    }
    if (loading) return;
    const row = items.find((t) => String(t.id) === String(openTaskIdFromUrl));
    if (row) {
      if (openedTaskIdRef.current === openTaskIdFromUrl) return;
      openedTaskIdRef.current = openTaskIdFromUrl;
      setFormData(rowToFormData(row));
      statusWhenEditStartedRef.current = row.status || 'open';
      setEditingId(row.id);
      setShowForm(true);
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete('taskId');
        return p;
      }, { replace: true });
    } else {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete('taskId');
        return p;
      }, { replace: true });
    }
  }, [openTaskIdFromUrl, loading, items, setSearchParams]);

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => comparePersonalRows(a, b, sortKey, sortDir));
    return copy;
  }, [items, sortKey, sortDir]);

  const handleSortClick = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      const dateKeys = [SORT_KEYS.due_date, SORT_KEYS.visible_from_date, SORT_KEYS.created_at];
      setSortDir(dateKeys.includes(key) ? 'desc' : 'asc');
    }
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setEditingId(null);
    setShowForm(false);
    statusWhenEditStartedRef.current = null;
  };

  const openAdd = () => {
    setFormData(emptyForm());
    setEditingId(null);
    statusWhenEditStartedRef.current = null;
    setShowForm(true);
  };

  const openEdit = (row) => {
    setFormData(rowToFormData(row));
    statusWhenEditStartedRef.current = row.status || 'open';
    setEditingId(row.id);
    setShowForm(true);
  };

  const buildPayload = () => {
    const rep = formData.repeat_interval === 'weekly' || formData.repeat_interval === 'monthly' || formData.repeat_interval === 'quarterly'
      ? formData.repeat_interval
      : 'none';
    const maxRaw = formData.repeat_max_occurrences?.trim();
    let repeat_max_occurrences = maxRaw ? parseInt(maxRaw, 10) : null;
    if (Number.isNaN(repeat_max_occurrences)) repeat_max_occurrences = null;
    return {
      title: formData.title.trim(),
      description: formData.description?.trim() || null,
      due_date: formData.due_date || null,
      visible_from_date: formData.visible_from_date || null,
      repeat_interval: rep,
      repeat_end_on: rep !== 'none' && formData.repeat_end_on ? formData.repeat_end_on : null,
      repeat_max_occurrences: rep !== 'none' ? repeat_max_occurrences : null,
    };
  };

  const performSave = async (completeOpts) => {
    const payload = buildPayload();
    if (editingId) {
      const body = {
        ...payload,
        status: formData.status,
      };
      if (completeOpts?.create_follow_up) {
        body.create_follow_up = true;
        body.follow_up_due_date = completeOpts.follow_up_due_date || null;
      }
      await personalMyTodoAPI.update(editingId, body);
    } else {
      await personalMyTodoAPI.create(payload);
    }
    await load();
    resetForm();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim()) {
      alert('Title is required.');
      return;
    }
    const rep = formData.repeat_interval === 'weekly' || formData.repeat_interval === 'monthly' || formData.repeat_interval === 'quarterly';
    if (rep && !formData.due_date) {
      alert('Due date is required when repeat is set.');
      return;
    }
    if (
      editingId
      && formData.status === 'done'
      && statusWhenEditStartedRef.current === 'open'
    ) {
      setFollowUpChecked(false);
      setFollowUpDueDate(addCalendarDaysLocal(30));
      setCompleteModalOpen(true);
      return;
    }
    setSaving(true);
    try {
      await performSave(null);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const confirmCompleteAndSave = async () => {
    setCompleteModalOpen(false);
    setSaving(true);
    try {
      await performSave({
        create_follow_up: followUpChecked,
        follow_up_due_date: followUpChecked ? followUpDueDate : null,
      });
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const cancelCompleteModal = () => {
    setCompleteModalOpen(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this to-do?')) return;
    try {
      await personalMyTodoAPI.delete(id);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to delete');
    }
  };

  const actions = canCreate && !showForm ? (
    <button type="button" className="personal-my-todos-btn-add" onClick={openAdd}>
      + Add To-Do
    </button>
  ) : null;

  return (
    <div className="personal-my-todos-page">
      <ContentHeader
        title="My To-Dos"
        titleHint="Not client-specific reminders for you. For anything tied to a client or episode (including work-task types), open the client and use My To-Dos there."
        actions={actions}
      />

      <p className="personal-my-todos-one-liner">
        Items here are labeled <strong>General</strong> on the dashboard. No client, referral, or work-task fields—by design.
      </p>

      <div className="personal-my-todos-toolbar">
        <label className="personal-my-todos-show-done">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
          />
          Show Done &amp; Canceled
        </label>
      </div>

      {error && <p className="personal-my-todos-error">{error}</p>}
      {loading && <p className="personal-my-todos-loading">Loading…</p>}

      {showForm && (
        <div className="personal-my-todos-form">
          <h3>{editingId ? 'Edit To-Do' : 'Add To-Do'}</h3>
          <form onSubmit={handleSave}>
            {editingId && (
              <div className="personal-my-todos-form-actions personal-my-todos-form-actions--top">
                <button type="button" className="btn-cancel" onClick={resetForm}>Cancel</button>
                <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            )}
            <div className="personal-my-todos-form-grid">
              <div className="form-group form-group-full">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  maxLength={500}
                  required
                />
              </div>
              <div className="form-group form-group-full">
                <label>Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group">
                <label>Due date</label>
                <input
                  type="date"
                  value={formData.due_date || ''}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Visible from</label>
                <input
                  type="date"
                  value={formData.visible_from_date || ''}
                  onChange={(e) => setFormData({ ...formData, visible_from_date: e.target.value })}
                  title="Shows on your dashboard on or after this date"
                />
              </div>
              <div className="form-group form-group-full">
                <label htmlFor="pm-repeat-interval">Repeat</label>
                <select
                  id="pm-repeat-interval"
                  value={formData.repeat_interval}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const next = { ...prev, repeat_interval: v };
                      if ((v === 'weekly' || v === 'monthly' || v === 'quarterly') && !prev.repeat_end_on) {
                        next.repeat_end_on = defaultOneYearFromTodayLocal();
                      }
                      if (v === 'none') {
                        next.repeat_end_on = '';
                        next.repeat_max_occurrences = '';
                      }
                      return next;
                    });
                  }}
                >
                  <option value="none">None</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
                <p className="personal-my-todos-help">
                  After you mark an item done, the next open item uses the completion date plus one week, one month, or three months. The series defaults to ending one year from when repeat was set unless you change the end date; you can also cap it after N completions.
                </p>
              </div>
              {(formData.repeat_interval === 'weekly' || formData.repeat_interval === 'monthly' || formData.repeat_interval === 'quarterly') && (
                <>
                  <div className="form-group">
                    <label>Series end date</label>
                    <input
                      type="date"
                      value={formData.repeat_end_on || ''}
                      onChange={(e) => setFormData({ ...formData, repeat_end_on: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>End after N completions</label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={formData.repeat_max_occurrences}
                      onChange={(e) => setFormData({ ...formData, repeat_max_occurrences: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </>
              )}
              {editingId && (
                <div className="form-group form-group-full">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="open">Open</option>
                    <option value="done">Done</option>
                    <option value="canceled">Canceled</option>
                  </select>
                </div>
              )}
            </div>
            <div className="personal-my-todos-form-actions">
              <button type="button" className="btn-cancel" onClick={resetForm}>Cancel</button>
              <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {!loading && !error && (
        <div className="personal-my-todos-table-wrap">
          {items.length === 0 ? (
            <p className="personal-my-todos-empty">
              No personal to-dos yet.
              {canCreate && ' Click “Add To-Do” to add one.'}
            </p>
          ) : (
            <table className="personal-my-todos-table">
              <thead>
                <tr>
                  <th>
                    <button
                      type="button"
                      className="personal-my-todos-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.title)}
                    >
                      Title
                      {sortKey === SORT_KEYS.title ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="personal-my-todos-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.status)}
                    >
                      Status
                      {sortKey === SORT_KEYS.status ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="personal-my-todos-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.due_date)}
                    >
                      Due
                      {sortKey === SORT_KEYS.due_date ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th>Repeat</th>
                  <th>
                    <button
                      type="button"
                      className="personal-my-todos-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.visible_from_date)}
                    >
                      Visible from
                      {sortKey === SORT_KEYS.visible_from_date ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="personal-my-todos-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.created_at)}
                    >
                      Created
                      {sortKey === SORT_KEYS.created_at ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th className="th-note">Desc</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((row) => (
                  <tr
                    key={row.id}
                    className={canUpdate ? 'personal-my-todos-row-clickable' : ''}
                    onClick={canUpdate ? () => openEdit(row) : undefined}
                    role={canUpdate ? 'button' : undefined}
                    tabIndex={canUpdate ? 0 : undefined}
                    onKeyDown={canUpdate ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(row); } } : undefined}
                  >
                    <td className="personal-my-todos-title-cell">{row.title || '—'}</td>
                    <td>{row.status === 'open' ? 'Open' : row.status === 'done' ? 'Done' : 'Canceled'}</td>
                    <td>{formatDisplayDate(row.due_date)}</td>
                    <td>{repeatLabel(row.repeat_interval)}</td>
                    <td>{formatDisplayDate(row.visible_from_date)}</td>
                    <td>{formatDisplayDate(row.created_at)}</td>
                    <td className="note-cell" title={row.description || undefined}>
                      {row.description ? '📝' : '—'}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {canDelete && (
                        <DeleteIconButton
                          className="btn-delete-small"
                          onClick={() => handleDelete(row.id)}
                          title="Remove"
                          aria-label="Remove"
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {completeModalOpen && (
        <div
          className="personal-my-todos-modal-overlay"
          role="presentation"
          onClick={cancelCompleteModal}
          onKeyDown={(e) => { if (e.key === 'Escape') cancelCompleteModal(); }}
        >
          <div
            className="personal-my-todos-modal"
            role="dialog"
            aria-labelledby="pm-complete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="pm-complete-title">Mark done</h3>
            <p className="personal-my-todos-modal-lead">
              Create a one-time follow-up with the same details? Default due date is 30 days from today.
            </p>
            <label className="personal-my-todos-modal-check">
              <input
                type="checkbox"
                checked={followUpChecked}
                onChange={(e) => setFollowUpChecked(e.target.checked)}
              />
              Create follow-up
            </label>
            {followUpChecked && (
              <div className="form-group" style={{ marginTop: 12 }}>
                <label htmlFor="pm-follow-due">Follow-up due date</label>
                <input
                  id="pm-follow-due"
                  type="date"
                  value={followUpDueDate}
                  onChange={(e) => setFollowUpDueDate(e.target.value)}
                />
              </div>
            )}
            <div className="personal-my-todos-modal-actions">
              <button type="button" className="btn-cancel" onClick={cancelCompleteModal}>Back</button>
              <button type="button" className="btn-save" onClick={confirmCompleteAndSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save as done'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PersonalMyTodos;
